import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Calendar, MapPin, ExternalLink, Play, Headphones, FileText, X } from 'lucide-react';
import { resolveImg } from '../lib/img';

// Extract a YouTube video ID from common URL formats
const getYouTubeId = (url = '') => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

// Resolve the best thumbnail: explicit thumbnail, else auto YouTube thumbnail
const getMediaThumb = (item) => {
  if (item.thumbnail_url) return resolveImg(item.thumbnail_url);
  const ytId = getYouTubeId(item.url);
  return ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : '';
};

// Parse "1:30", "1:02:03" or plain "90" into total seconds
const parseTimeToSeconds = (val) => {
  if (val == null || val === '') return null;
  const str = String(val).trim();
  if (/^\d+$/.test(str)) return parseInt(str, 10);
  const parts = str.split(':').map((p) => parseInt(p, 10));
  if (parts.some(isNaN)) return null;
  return parts.reduce((acc, p) => acc * 60 + p, 0);
};

// Build a YouTube embed URL honouring optional start/end timestamps
const buildYouTubeEmbed = (item) => {
  const id = getYouTubeId(item.url);
  if (!id) return null;
  const params = new URLSearchParams({ autoplay: '1', rel: '0', modestbranding: '1' });
  const start = parseTimeToSeconds(item.start_time);
  const end = parseTimeToSeconds(item.end_time);
  if (start != null) params.set('start', String(start));
  if (end != null) params.set('end', String(end));
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
};

const MediaEvents = () => {
  const [articles, setArticles] = useState([]);
  const [media, setMedia] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [regEvent, setRegEvent] = useState(null);
  const [regForm, setRegForm] = useState({ name: '', email: '', organisation: '' });
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [regError, setRegError] = useState('');
  const [regDone, setRegDone] = useState(false);

  // Fetch articles, media, and events from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [articlesRes, mediaRes, eventsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/articles`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events`)
        ]);

        const [articlesData, mediaData, eventsData] = await Promise.all([
          articlesRes.json(),
          mediaRes.json(),
          eventsRes.json()
        ]);

        setArticles(articlesData.articles || []);
        setMedia(mediaData.media || []);
        setEvents(eventsData.events || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Scroll to top when component mounts or article changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedArticle]);

  const openRegistration = (event) => {
    setRegEvent(event);
    setRegForm({ name: '', email: '', organisation: '' });
    setRegError('');
    setRegDone(false);
  };

  const handleEventRegistration = async (e) => {
    e.preventDefault();
    setRegSubmitting(true);
    setRegError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: regForm.name,
          email: regForm.email,
          organisation: regForm.organisation,
          event_id: regEvent?.id,
          event_name: regEvent?.title
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setRegDone(true);
      } else {
        setRegError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setRegError('Failed to connect. Please try again later.');
      console.error('Event registration error:', err);
    } finally {
      setRegSubmitting(false);
    }
  };

  const mediaIcons = {
    Podcast: Headphones,
    'Conference Talk': Play,
    Video: Play,
    Interview: Play
  };

  if (selectedArticle) {
    return (
      <div>
        {/* Article Detail View */}
        <section
          style={{
            background: '#FFFFFF',
            padding: '120px 40px 80px'
          }}
        >
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <button
              onClick={() => setSelectedArticle(null)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#9A7B2E',
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: '32px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              ← Back to Media & Events
            </button>
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '500',
                marginBottom: '24px',
                color: '#111111',
                lineHeight: '1.2'
              }}
            >
              {selectedArticle.title}
            </h1>
            <div
              style={{
                display: 'flex',
                gap: '24px',
                marginBottom: '40px',
                fontSize: '0.95rem',
                color: '#666666'
              }}
            >
              <span>By {selectedArticle.author}</span>
              <span>•</span>
              <span>{new Date(selectedArticle.date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}</span>
            </div>
            {/* Featured Image */}
            {selectedArticle.image_url ? (
              <img
                src={selectedArticle.image_url}
                alt={selectedArticle.title}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '500px',
                  objectFit: 'cover',
                  marginBottom: '40px',
                  borderRadius: '8px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className="placeholder-box"
              style={{
                width: '100%',
                height: '400px',
                marginBottom: '40px',
                borderRadius: '8px',
                fontSize: '1.1rem',
                display: selectedArticle.image_url ? 'none' : 'flex'
              }}
            >
              Article Featured Image
            </div>
            {/* Article Content (rich text or legacy plain text) */}
            <div
              className="article-content"
              style={{
                fontSize: '1.15rem',
                lineHeight: '1.9',
                color: '#333333'
              }}
            >
              {/<[a-z][\s\S]*>/i.test(selectedArticle.content) ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedArticle.content, { ADD_ATTR: ['target'] }) }} />
              ) : (
                selectedArticle.content.split('\n\n').map((paragraph, index) => (
                  <p key={index} style={{ marginBottom: '24px' }}>
                    {paragraph}
                  </p>
                ))
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section
        style={{
          background: '#FFFFFF',
          padding: '120px 40px 80px'
        }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: '500',
              marginBottom: '24px',
              color: '#111111'
            }}
          >
            Media & Events
          </h1>
          <p
            style={{
              fontSize: '1.25rem',
              lineHeight: '1.8',
              color: '#333333'
            }}
          >
            Insights, conversations, and opportunities to engage with the ideas in The Goldilocks Zone.
          </p>
        </div>
      </section>

      {/* Articles Section */}
      <section className="section-container spectrum-wash">
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 className="section-title" style={{ marginBottom: '60px' }}>
            <FileText size={36} style={{ display: 'inline-block', marginRight: '12px', verticalAlign: 'middle' }} />
            Articles
          </h2>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#999999' }}>
              <p>Loading articles...</p>
            </div>
          ) : articles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#999999' }}>
              <p>No articles available yet. Check back soon!</p>
            </div>
          ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '40px'
            }}
          >
            {articles.map((article) => (
              <div
                key={article.id}
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={() => setSelectedArticle(article)}
              >
                {/* Article Thumbnail */}
                {article.image_url ? (
                  <img
                    src={article.image_url}
                    alt={article.title}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      borderBottom: '1px solid #E0E0E0'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="placeholder-box"
                  style={{
                    height: '200px',
                    borderRadius: '0',
                    border: 'none',
                    borderBottom: '1px solid #E0E0E0',
                    display: article.image_url ? 'none' : 'flex'
                  }}
                >
                  Article Image
                </div>
                <div style={{ padding: '24px' }}>
                  <h3
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.4rem',
                      fontWeight: '600',
                      marginBottom: '12px',
                      color: '#111111',
                      lineHeight: '1.3'
                    }}
                  >
                    {article.title}
                  </h3>
                  <div
                    style={{
                      fontSize: '0.9rem',
                      color: '#666666',
                      marginBottom: '16px'
                    }}
                  >
                    {article.author} • {new Date(article.date).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </div>
                  <p
                    style={{
                      fontSize: '1rem',
                      lineHeight: '1.7',
                      color: '#333333',
                      marginBottom: '20px'
                    }}
                  >
                    {(() => {
                      const s = (article.summary || '').replace(/\s+/g, ' ').trim();
                      return s.length > 160 ? s.slice(0, 160).trimEnd() + '…' : s;
                    })()}
                  </p>
                  <span
                    style={{
                      color: '#9A7B2E',
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    Read Article <ExternalLink size={16} />
                  </span>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Media Section */}
      <section
        style={{
          backgroundColor: '#FFFFFF',
          padding: '100px 40px'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 className="section-title" style={{ marginBottom: '60px' }}>
            <Play size={36} style={{ display: 'inline-block', marginRight: '12px', verticalAlign: 'middle' }} />
            Media
          </h2>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#999999' }}>
              <p>Loading media...</p>
            </div>
          ) : media.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#999999' }}>
              <p>No media available yet. Check back soon!</p>
            </div>
          ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 360px))',
              justifyContent: 'start',
              gap: '32px'
            }}
          >
            {media.map((item) => {
              const IconComponent = mediaIcons[item.type] || Play;
              const thumb = getMediaThumb(item);
              return (
                <div
                  key={item.id}
                  data-testid={`media-card-${item.id}`}
                  onClick={() => {
                    const embed = buildYouTubeEmbed(item);
                    if (embed) {
                      setActiveVideo({ src: embed, title: item.title, description: item.description, platform: item.platform, date: item.date, type: item.type });
                    } else if (item.url) {
                      window.open(item.url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.3s ease',
                    cursor: item.url ? 'pointer' : 'default'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  {thumb ? (
                    <div style={{ position: 'relative', height: '200px', overflow: 'hidden' }}>
                      <img
                        src={thumb}
                        alt={item.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => {
                          e.currentTarget.parentElement.style.display = 'none';
                          e.currentTarget.parentElement.nextSibling.style.display = 'flex';
                        }}
                      />
                      {item.url && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          <div
                            style={{
                              width: '64px',
                              height: '64px',
                              borderRadius: '50%',
                              backgroundColor: 'rgba(17, 17, 17, 0.75)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Play size={28} color="#F4F1E8" fill="#F4F1E8" style={{ marginLeft: '3px' }} />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                  <div
                    className="placeholder-box"
                    style={{
                      height: '200px',
                      borderRadius: '0',
                      border: 'none',
                      position: 'relative',
                      display: thumb ? 'none' : 'flex'
                    }}
                  >
                    <IconComponent size={48} color="#111111" />
                    <div style={{ marginTop: '12px' }}>{item.type}</div>
                  </div>
                  <div style={{ padding: '24px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        backgroundColor: '#111111',
                        color: '#F4F1E8',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        borderRadius: '4px',
                        marginBottom: '12px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {item.type}
                    </span>
                    <h3
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: '1.3rem',
                        fontWeight: '600',
                        marginBottom: '8px',
                        color: '#111111'
                      }}
                    >
                      {item.title}
                    </h3>
                    <p
                      style={{
                        fontSize: '0.9rem',
                        color: '#666666',
                        marginBottom: '12px'
                      }}
                    >
                      {item.platform} • {new Date(item.date).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                    <p
                      style={{
                        fontSize: '1rem',
                        lineHeight: '1.6',
                        color: '#333333'
                      }}
                    >
                      {(() => {
                        const s = (item.description || '').replace(/\s+/g, ' ').trim();
                        return s.length > 160 ? s.slice(0, 160).trimEnd() + '…' : s;
                      })()}
                    </p>
                    {buildYouTubeEmbed(item) && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          marginTop: '14px',
                          color: '#9A7B2E',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        <Play size={15} fill="#9A7B2E" /> Watch &amp; read more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </section>

      {/* Events Section */}
      <section className="section-container spectrum-wash">
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 className="section-title" style={{ marginBottom: '60px' }}>
            <Calendar size={36} style={{ display: 'inline-block', marginRight: '12px', verticalAlign: 'middle' }} />
            Upcoming Events
          </h2>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#999999' }}>
              <p>Loading events...</p>
            </div>
          ) : events.filter(e => e.type === 'upcoming').length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#999999' }}>
              <p>No upcoming events at this time. Check back soon!</p>
            </div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {events.filter(e => e.type === 'upcoming').map((event) => (
              <div
                key={event.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: '32px',
                  padding: '32px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E7E0D4',
                  borderLeft: '4px solid #C9A24B',
                  borderRadius: '8px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                  alignItems: 'center'
                }}
              >
                <div
                  style={{
                    textAlign: 'center',
                    minWidth: '100px'
                  }}
                >
                  <div
                    style={{
                      fontSize: '2rem',
                      fontWeight: '500',
                      color: '#9A7B2E',
                      fontFamily: 'var(--font-display)'
                    }}
                  >
                    {new Date(event.date).getDate()}
                  </div>
                  <div
                    style={{
                      fontSize: '1rem',
                      textTransform: 'uppercase',
                      color: '#666666',
                      fontWeight: '600'
                    }}
                  >
                    {new Date(event.date).toLocaleDateString('en-GB', { month: 'short' })}
                  </div>
                </div>
                <div>
                  <h3
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginBottom: '12px',
                      color: '#111111'
                    }}
                  >
                    {event.title}
                  </h3>
                  <p
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '1rem',
                      color: '#666666',
                      marginBottom: '12px'
                    }}
                  >
                    <MapPin size={18} />
                    {event.location}
                  </p>
                  <p
                    style={{
                      fontSize: '1rem',
                      lineHeight: '1.7',
                      color: '#333333',
                      whiteSpace: 'pre-line'
                    }}
                  >
                    {event.description}
                  </p>
                </div>
                {event.registration_open ? (
                  event.sold_out ? (
                    <span
                      data-testid={`event-sold-out-${event.id}`}
                      style={{ display: 'inline-block', padding: '8px 16px', fontSize: '0.9rem', fontWeight: '600', color: '#9A7B2E', backgroundColor: '#F4F1E8', border: '1px solid #E7E0D4', borderRadius: '20px' }}
                    >
                      Fully booked
                    </span>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
                      {typeof event.spots_left === 'number' && (
                        <span data-testid={`event-spots-left-${event.id}`} style={{ fontSize: '0.9rem', fontWeight: '600', color: '#9A7B2E' }}>
                          {event.spots_left} {event.spots_left === 1 ? 'spot' : 'spots'} left
                        </span>
                      )}
                      <button
                        className="btn-primary"
                        data-testid={`event-register-btn-${event.id}`}
                        onClick={() => openRegistration(event)}
                      >
                        Register
                      </button>
                    </div>
                  )
                ) : (
                  <span
                    data-testid={`event-invite-only-${event.id}`}
                    style={{
                      display: 'inline-block',
                      padding: '8px 16px',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#6B6760',
                      backgroundColor: '#F4F1E8',
                      border: '1px solid #E7E0D4',
                      borderRadius: '20px'
                    }}
                  >
                    By invitation only
                  </span>
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      </section>


      {/* Per-event Registration Modal */}
      {regEvent && (
        <div
          data-testid="event-registration-modal"
          onClick={() => setRegEvent(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 2000, backgroundColor: 'rgba(17, 17, 17, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#FFFFFF', borderRadius: '10px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '24px 28px', borderBottom: '1px solid #F0F0F0' }}>
              <div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: '600', margin: 0, color: '#111111' }}>Register</h3>
                <p style={{ fontSize: '0.95rem', color: '#6B6760', margin: '6px 0 0' }}>{regEvent.title}</p>
              </div>
              <button onClick={() => setRegEvent(null)} data-testid="event-registration-close" aria-label="Close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#666666' }}><X size={22} /></button>
            </div>

            <div style={{ padding: '28px' }}>
              {regDone ? (
                <div data-testid="event-registration-success" style={{ padding: '18px', backgroundColor: '#E8F5E9', border: '1px solid #4CAF50', borderRadius: '8px', color: '#2E7D32', textAlign: 'center' }}>
                  Thank you, your registration for {regEvent.title} has been received.
                </div>
              ) : (
                <form onSubmit={handleEventRegistration} style={{ display: 'grid', gap: '18px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '6px', color: '#111111' }}>Name *</label>
                    <input type="text" required value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} data-testid="event-reg-name" style={{ width: '100%', padding: '12px 14px', border: '1px solid #E0E0E0', borderRadius: '6px', fontSize: '1rem', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '6px', color: '#111111' }}>Email *</label>
                    <input type="email" required value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} data-testid="event-reg-email" style={{ width: '100%', padding: '12px 14px', border: '1px solid #E0E0E0', borderRadius: '6px', fontSize: '1rem', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '6px', color: '#111111' }}>Organisation</label>
                    <input type="text" value={regForm.organisation} onChange={(e) => setRegForm({ ...regForm, organisation: e.target.value })} data-testid="event-reg-organisation" placeholder="Optional" style={{ width: '100%', padding: '12px 14px', border: '1px solid #E0E0E0', borderRadius: '6px', fontSize: '1rem', boxSizing: 'border-box' }} />
                  </div>
                  {regError && <p data-testid="event-reg-error" style={{ color: '#cc0000', fontSize: '0.9rem', margin: 0 }}>{regError}</p>}
                  <button type="submit" disabled={regSubmitting} data-testid="event-reg-submit" className="btn-primary" style={{ padding: '14px 24px', fontSize: '1rem', fontWeight: '600', opacity: regSubmitting ? 0.6 : 1, cursor: regSubmitting ? 'not-allowed' : 'pointer' }}>
                    {regSubmitting ? 'Submitting...' : 'Submit Registration'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Inline Video Modal */}
      {activeVideo && (
        <div
          data-testid="video-modal"
          onClick={() => setActiveVideo(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            backgroundColor: 'rgba(17, 17, 17, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'relative', width: '100%', maxWidth: '960px', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <button
              data-testid="video-modal-close"
              onClick={() => setActiveVideo(null)}
              aria-label="Close video"
              style={{
                position: 'absolute',
                top: '-44px',
                right: 0,
                background: 'transparent',
                border: 'none',
                color: '#F4F1E8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.95rem',
                fontWeight: '600'
              }}
            >
              Close <X size={22} />
            </button>
            <div
              style={{
                position: 'relative',
                paddingBottom: '56.25%',
                height: 0,
                borderRadius: '8px 8px 0 0',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                backgroundColor: '#000000'
              }}
            >
              <iframe
                title={activeVideo.title}
                src={activeVideo.src}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            </div>
            <div
              data-testid="video-modal-details"
              style={{
                backgroundColor: '#FFFFFF',
                padding: '28px 32px',
                borderRadius: '0 0 8px 8px'
              }}
            >
              {activeVideo.type && (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    backgroundColor: '#111111',
                    color: '#F4F1E8',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    borderRadius: '4px',
                    marginBottom: '14px',
                    textTransform: 'uppercase'
                  }}
                >
                  {activeVideo.type}
                </span>
              )}
              <h3
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: '#111111',
                  marginBottom: '8px',
                  lineHeight: '1.3'
                }}
              >
                {activeVideo.title}
              </h3>
              {(activeVideo.platform || activeVideo.date) && (
                <p style={{ fontSize: '0.9rem', color: '#666666', marginBottom: '18px' }}>
                  {activeVideo.platform}
                  {activeVideo.platform && activeVideo.date ? ' • ' : ''}
                  {activeVideo.date && new Date(activeVideo.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              )}
              <p style={{ fontSize: '1rem', lineHeight: '1.75', color: '#333333', whiteSpace: 'pre-line' }}>
                {activeVideo.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaEvents;
